import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import {
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { http } from '../../../api/http';

interface TrabajadorResponse {
  idTrabajador: number;
  idPersona: number;
  activo: boolean;
  personal?: {
    idPersona: number;
    personaContacto: string | null;
    identificacion: string;
  };
}

interface WaiterSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (idMozo: number) => void;
}

export const WaiterSelector: React.FC<WaiterSelectorProps> = ({ open, onClose, onSelect }) => {
  const [trabajadores, setTrabajadores] = useState<TrabajadorResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (open) {
      loadTrabajadores();
    }
  }, [open]);

  const loadTrabajadores = async () => {
    setLoading(true);
    try {
      const response = await http.get('/planilla/trabajador');
      const data: TrabajadorResponse[] = response.data ?? [];
      setTrabajadores(data.filter((t) => t.activo));
    } catch (error) {
      console.error('Error loading trabajadores:', error);
      setTrabajadores([]);
    } finally {
      setLoading(false);
    }
  };

  const getNombre = (t: TrabajadorResponse) =>
    t.personal?.personaContacto || t.personal?.identificacion || `ID: ${t.idPersona}`;

  const filtered = trabajadores.filter((t) => {
    const term = filter.toLowerCase();
    const nombre = getNombre(t).toLowerCase();
    return nombre.includes(term);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Asignar Mozo</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder="Buscar mozo..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          sx={{ mb: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length > 0 ? (
          <List dense>
            {filtered.map((t) => (
              <ListItemButton
                key={t.idPersona}
                onClick={() => onSelect(t.idPersona)}
              >
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary={getNombre(t)} />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            No se encontraron mozos
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaiterSelector;
